/**
 * Onboarding Tour Steps Configuration
 * Defines all onboarding tours and their steps for LegalSyncUp
 */

import type { OnboardingTour, OnboardingChecklistItem } from '@/types/onboarding';

/**
 * Dashboard Tour
 * Introduces users to the main dashboard features
 */
export const dashboardTour: OnboardingTour = {
  id: 'dashboard',
  name: 'Dashboard Tour',
  autoStart: true,
  priority: 1,
  steps: [
    {
      id: 'dashboard-welcome',
      targetSelector: '[data-onboarding="dashboard-welcome"]',
      title: 'Welcome to LegalSyncUp',
      description:
        'This is your dashboard where you can view all your Swiss entities, documents, and activities at a glance.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'dashboard-stats',
      targetSelector: '[data-onboarding="dashboard-stats"]',
      title: 'Key Metrics',
      description:
        'Monitor your entities, active projects, pending approvals, and team members. These cards provide quick insights into your legal operations.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'dashboard-activity',
      targetSelector: '[data-onboarding="dashboard-activity"]',
      title: 'Recent Activity',
      description:
        'Stay updated with real-time activity feed showing document changes, approvals, and team actions.',
      position: 'top',
      spotlight: true,
    },
    {
      id: 'dashboard-quick-actions',
      targetSelector: '[data-onboarding="dashboard-quick-actions"]',
      title: 'Quick Actions',
      description:
        'Access common tasks quickly. Create entities, upload documents, or invite team members with one click.',
      position: 'left',
      spotlight: true,
    },
  ],
};

/**
 * Entity Creation Tour
 * Guides users through creating their first Swiss entity
 */
export const entityCreationTour: OnboardingTour = {
  id: 'entity-creation',
  name: 'Create Your First Entity',
  priority: 2,
  steps: [
    {
      id: 'entity-form-intro',
      targetSelector: '[data-onboarding="entity-form"]',
      title: 'Swiss Entity Details',
      description:
        'Enter your Swiss entity information. We support all Swiss legal forms: AG, GmbH, Verein, Stiftung, and more.',
      position: 'right',
      spotlight: true,
    },
    {
      id: 'entity-uid',
      targetSelector: '[data-onboarding="entity-uid"]',
      title: 'UID Number',
      description:
        'Enter your Swiss UID number in the format CHE-XXX.XXX.XXX. We validate it against the commercial register format.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'entity-legal-form',
      targetSelector: '[data-onboarding="entity-legal-form"]',
      title: 'Legal Form',
      description:
        'Select your entity\'s legal structure. This determines compliance requirements and available features.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'entity-submit',
      targetSelector: '[data-onboarding="entity-submit"]',
      title: 'Create Entity',
      description:
        'Once all required fields are filled, click here to create your entity and start managing your legal documents.',
      position: 'top',
      spotlight: true,
    },
  ],
};

/**
 * Document Management Tour
 * Shows users how to upload and manage documents
 */
export const documentManagementTour: OnboardingTour = {
  id: 'document-management',
  name: 'Document Management',
  priority: 3,
  steps: [
    {
      id: 'document-upload',
      targetSelector: '[data-onboarding="document-upload"]',
      title: 'Upload Documents',
      description:
        'Upload legal documents, contracts, and compliance files. We support PDF, Word, and other common formats.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'document-list',
      targetSelector: '[data-onboarding="document-list"]',
      title: 'Document Library',
      description:
        'View all your documents organized by entity and project. Use filters to find what you need quickly.',
      position: 'top',
      spotlight: true,
    },
    {
      id: 'document-approval',
      targetSelector: '[data-onboarding="document-approval"]',
      title: 'Approval Workflows',
      description:
        'Documents can go through approval workflows with legal role templates. Track status and get notifications.',
      position: 'left',
      spotlight: true,
    },
    {
      id: 'document-version',
      targetSelector: '[data-onboarding="document-version"]',
      title: 'Version Control',
      description:
        'Every document change is tracked with immutable audit trails. View history and restore previous versions.',
      position: 'bottom',
      spotlight: true,
    },
  ],
};

/**
 * Team Collaboration Tour
 * Introduces team management features
 */
export const teamCollaborationTour: OnboardingTour = {
  id: 'team-collaboration',
  name: 'Team Collaboration',
  priority: 4,
  steps: [
    {
      id: 'team-invite',
      targetSelector: '[data-onboarding="team-invite"]',
      title: 'Invite Team Members',
      description:
        'Add colleagues to collaborate on entities and documents. Assign roles based on Swiss legal templates.',
      position: 'bottom',
      spotlight: true,
    },
    {
      id: 'team-roles',
      targetSelector: '[data-onboarding="team-roles"]',
      title: 'Role Management',
      description:
        'Control access with role-based permissions. From viewers to administrators, each role has specific capabilities.',
      position: 'left',
      spotlight: true,
    },
    {
      id: 'team-activity',
      targetSelector: '[data-onboarding="team-activity"]',
      title: 'Team Activity',
      description:
        'See what your team is working on in real-time. Collaboration features include comments and notifications.',
      position: 'top',
      spotlight: true,
    },
  ],
};

/**
 * Settings Tour
 * Shows users how to configure their account and preferences
 */
export const settingsTour: OnboardingTour = {
  id: 'settings',
  name: 'Settings & Preferences',
  priority: 5,
  steps: [
    {
      id: 'settings-profile',
      targetSelector: '[data-onboarding="settings-profile"]',
      title: 'Profile Settings',
      description:
        'Update your personal information, notification preferences, and language settings.',
      position: 'right',
      spotlight: true,
    },
    {
      id: 'settings-security',
      targetSelector: '[data-onboarding="settings-security"]',
      title: 'Security Settings',
      description:
        'Enable two-factor authentication and manage your security preferences for Swiss legal compliance.',
      position: 'right',
      spotlight: true,
    },
    {
      id: 'settings-theme',
      targetSelector: '[data-onboarding="settings-theme"]',
      title: 'Theme Selection',
      description:
        'Choose from our curated theme presets to personalize your workspace appearance.',
      position: 'left',
      spotlight: true,
    },
  ],
};

/**
 * All Available Tours
 */
export const allTours: OnboardingTour[] = [
  dashboardTour,
  entityCreationTour,
  documentManagementTour,
  teamCollaborationTour,
  settingsTour,
];

/**
 * Onboarding Checklist Items
 * Tasks for new users to complete during onboarding
 */
export const onboardingChecklist: OnboardingChecklistItem[] = [
  {
    id: 'create-entity',
    title: 'Create your first entity',
    description: 'Add a Swiss legal entity to start managing documents',
    completed: false,
  },
  {
    id: 'upload-document',
    title: 'Upload a document',
    description: 'Upload your first legal document or contract',
    completed: false,
  },
  {
    id: 'invite-team-member',
    title: 'Invite team members',
    description: 'Add colleagues to collaborate on your entities',
    completed: false,
    optional: true,
  },
  {
    id: 'configure-profile',
    title: 'Configure your profile',
    description: 'Set up your profile and preferences',
    completed: false,
    optional: true,
  },
  {
    id: 'explore-features',
    title: 'Explore key features',
    description: 'Take a tour of document management and workflows',
    completed: false,
  },
];

/**
 * Get tour by ID
 */
export const getTourById = (tourId: string): OnboardingTour | undefined => {
  return allTours.find((tour) => tour.id === tourId);
};

/**
 * Get next recommended tour based on completed tours
 */
export const getNextRecommendedTour = (
  completedTourIds: string[]
): OnboardingTour | undefined => {
  const incompleteTours = allTours
    .filter((tour) => !completedTourIds.includes(tour.id))
    .sort((a, b) => (a.priority || 999) - (b.priority || 999));

  return incompleteTours[0];
};
